import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
} from '@mui/material';
import SearchableSelect from 'shared/components/SearchableSelect';
import ClockLoader from 'shared/components/ClockLoader';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-hot-toast';
import { fetchTenantAssignmentsWithServices } from 'shared/api/services/tenantAssignmentsApi';
import { useAuth } from 'shared/hooks/useAuth';
import { resolveServiceLabel } from 'platform/rbac/Permissions';

const validationSchema = yup.object({
  name: yup.string().trim().required('Name is required'),
  serviceId: yup.string().trim().required('Service is required'),
  description: yup.string().trim(),
});

const EditGroupDialog = ({ open, onClose, onSuccess, group, updateGroup }) => {
  const [submitting, setSubmitting] = useState(false);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const { user } = useAuth();
  const tenantId =
    user?.data?.tenantId != null && String(user.data.tenantId).trim() !== ''
      ? String(user.data.tenantId).trim()
      : '';

  useEffect(() => {
    if (!open) setSubmitting(false);
  }, [open]);

  useEffect(() => {
    let active = true;

    const loadTenantServices = async () => {
      if (!open || !tenantId) {
        if (active) {
          setServiceOptions([]);
          setServicesLoading(false);
        }
        return;
      }

      setServicesLoading(true);
      try {
        const assignments = await fetchTenantAssignmentsWithServices(tenantId);
        if (!active) return;
        const mapped = assignments
          .map((assignment) => ({
            serviceId: assignment?.serviceId != null ? String(assignment.serviceId).trim() : '',
            serviceCode:
              assignment?.serviceCode != null ? String(assignment.serviceCode).trim() : '',
            serviceName:
              assignment?.serviceName != null ? String(assignment.serviceName).trim() : '',
          }))
          .filter((assignment) => assignment.serviceId !== '')
          .filter(
            (assignment, index, arr) =>
              arr.findIndex((item) => item.serviceId === assignment.serviceId) === index
          );
        setServiceOptions(mapped);
      } catch (error) {
        if (!active) return;
        setServiceOptions([]);
        const err =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to load tenant services';
        toast.error(typeof err === 'string' ? err : 'Failed to load tenant services');
      } finally {
        if (active) {
          setServicesLoading(false);
        }
      }
    };

    loadTenantServices();

    return () => {
      active = false;
    };
  }, [open, tenantId]);

  const sortedServiceOptions = useMemo(() => {
    const baseOptions = [...serviceOptions];
    const currentServiceId =
      group?.serviceId != null && String(group.serviceId).trim() !== ''
        ? String(group.serviceId).trim()
        : '';

    if (
      currentServiceId &&
      !baseOptions.some((option) => option.serviceId === currentServiceId)
    ) {
      baseOptions.push({
        serviceId: currentServiceId,
        serviceCode: group?.serviceCode ?? '',
        serviceName:
          group?.serviceName ??
          resolveServiceLabel(group?.serviceCode ?? ''),
      });
    }

    return baseOptions.sort((a, b) =>
      String(a.serviceName || a.serviceCode).localeCompare(String(b.serviceName || b.serviceCode))
    );
  }, [group?.serviceCode, group?.serviceId, group?.serviceName, serviceOptions]);

  if (!group) return null;

  const initialValues = {
    name: group.name ?? '',
    serviceId: group.serviceId ?? '',
    description: group.description ?? '',
  };

  const handleSubmit = async (values, { resetForm }) => {
    setSubmitting(true);
    try {
      const payload = {
        id: group.id,
        name: values.name.trim(),
        serviceId: values.serviceId.trim(),
        description: values.description?.trim() || null,
        status: typeof group.status === 'string' ? group.status : 'ACTIVE',
      };
      await updateGroup(payload);
      toast.success('Group updated successfully');
      resetForm();
      onSuccess?.();
      onClose();
    } catch (error) {
      const err =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to update group';
      toast.error(typeof err === 'string' ? err : 'Failed to update group');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>
        Edit group
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
          <Form>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2} pt={1}>
                <TextField
                  fullWidth
                  name="name"
                  label="Name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
                <SearchableSelect
                  name="serviceId"
                  label="Service"
                  value={values.serviceId}
                  onChange={(next) => setFieldValue('serviceId', next)}
                  onBlur={handleBlur}
                  disabled={servicesLoading}
                  error={touched.serviceId && Boolean(errors.serviceId)}
                  helperText={
                    touched.serviceId && errors.serviceId
                      ? errors.serviceId
                      : servicesLoading
                        ? 'Loading tenant services...'
                        : sortedServiceOptions.length === 0
                          ? 'No services are assigned to this tenant yet'
                          : 'Select the tenant service this group belongs to'
                  }
                  options={sortedServiceOptions.map((option) => ({
                    value: option.serviceId,
                    label: option.serviceName || option.serviceCode || option.serviceId,
                  }))}
                />
                <TextField
                  fullWidth
                  name="description"
                  label="Description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  multiline
                  minRows={2}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="secondary" disabled={submitting}>
                {submitting ? <ClockLoader inline size={22} /> : 'Save'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditGroupDialog;
